import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { useContact } from "./ContactProvider";

const STORAGE_KEY = "ls_exit_intent_v1";

export default function ExitIntent() {
  const [open, setOpen] = useState(false);
  const { open: openContact } = useContact();

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(STORAGE_KEY)) return;

    let armed = false;
    // Don't trigger immediately on page load — wait a few seconds.
    const armTimer = setTimeout(() => {
      armed = true;
    }, 8000);

    const onMouseLeave = (e) => {
      if (!armed) return;
      // Only trigger when mouse exits the top of the viewport.
      if (e.clientY > 5) return;
      if (e.relatedTarget || e.toElement) return;
      sessionStorage.setItem(STORAGE_KEY, "1");
      setOpen(true);
    };

    document.addEventListener("mouseleave", onMouseLeave);
    return () => {
      clearTimeout(armTimer);
      document.removeEventListener("mouseleave", onMouseLeave);
    };
  }, []);

  const close = () => setOpen(false);

  return (
    <AnimatePresence>
      {open && (
        <motion.aside
          data-testid="exit-intent-banner"
          role="dialog"
          aria-label="Before you go"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-md z-[180] surface-card p-5 md:p-6 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)]"
        >
          <button
            type="button"
            onClick={close}
            data-testid="exit-intent-close"
            aria-label="Close"
            className="absolute top-3 right-3 w-8 h-8 inline-flex items-center justify-center border border-[#2E2E38] text-[#A3A3A3] hover:text-[#E3FF34] hover:border-[#E3FF34]/60 transition-colors"
          >
            <X size={14} />
          </button>

          <p className="font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.25em] text-[#E3FF34] mb-3 flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-[#E3FF34] inline-block pulse-halo" />
            Before you go
          </p>
          <h3 className="font-['Outfit'] text-lg md:text-xl text-[#F2F2F2] tracking-tight pr-8">
            Want a free 15-minute marketing audit?
          </h3>
          <p className="mt-2 font-['Outfit'] font-light text-[#A3A3A3] text-sm leading-relaxed">
            Mark will record a loom-style review of your site, ads or social — no
            pitch, just a take.
          </p>

          <div className="mt-5 flex items-center gap-3">
            <button
              type="button"
              onClick={() => {
                close();
                openContact({ service: "multiple", budget: "not-sure", message: "I'd like the free 15-minute audit." });
              }}
              data-testid="exit-intent-cta"
              className="btn-shimmer relative inline-flex items-center gap-2 bg-[#E3FF34] text-[#050505] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[11px] px-5 py-3 border border-[#E3FF34] hover:bg-transparent hover:text-[#E3FF34] transition-all duration-300"
            >
              <span className="relative z-10 inline-flex items-center gap-2">
                Claim audit
                <ArrowUpRight size={13} />
              </span>
            </button>
            <button
              type="button"
              onClick={close}
              className="font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[10px] text-[#A3A3A3] hover:text-[#F2F2F2] transition-colors"
            >
              No thanks
            </button>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
